import React, { useEffect, useRef, useState } from "react";
import classes from "./AIResponseSource.module.css";
import { useDispatch, useSelector } from "react-redux";
import { selectionsSliceActions } from "../store/slices/selectionsSlice";
import { getIso3ByCountryName } from "../util/countryLookup";
import { buildDtmApiUrl } from "../util/dtmApi";
import { getDataFreshness } from "../util/dataFreshness";
import ApiConfigModal from "./ApiConfigModal";

const parseJsonValue = (value) => {
  if (!value) return null;
  if (typeof value === "object") return value;

  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

const formatSourceDate = (value) => {
  if (!value) return "";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);

  return new Intl.DateTimeFormat("en-US", {
    day: "2-digit",
    month: "short",
    timeZone: "UTC",
    year: "numeric",
  })
    .format(date)
    .replace(",", "");
};

const FileIcon = () => (
  <svg viewBox="0 0 24 24" width="15" height="15" fill="none" aria-hidden="true">
    <path
      d="M14 3H7c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h10c1.1 0 2-.9 2-2V8l-5-5Z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M14 3v5h5M8 13h8M8 17h6"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const getOperationItems = (value) => {
  const parsedValue = parseJsonValue(value);

  if (Array.isArray(parsedValue)) return parsedValue;
  if (!parsedValue) return [];
  if (Array.isArray(parsedValue.operations)) return parsedValue.operations;
  if (Array.isArray(parsedValue.rounds)) return parsedValue.rounds;
  if (Array.isArray(parsedValue.data)) return parsedValue.data;

  return [parsedValue];
};

const getDetailItems = (row) => {
  const operationData = getOperationItems(row?.operation_data);
  const operationDetail = getOperationItems(row?.operation_detail);
  const details = operationData.length ? operationData : operationDetail;

  if (details.length) {
    return details.map((detailValue) => {
      const detail = parseJsonValue(detailValue) || {};

      return {
        country:
          detail.country ||
          detail.admin_0_name ||
          detail.c ||
          row?.admin_0_name ||
          row?.country ||
          row?.iso3 ||
          "",
        operation: detail.operation || detail.o || row?.operation || "",
        round:
          detail.round ||
          detail.round_number ||
          detail.r ||
          row?.round ||
          row?.round_number ||
          "",
        date:
          detail.reporting_date ||
          detail.date ||
          detail.d ||
          row?.reporting_date ||
          "",
        freshness: getDataFreshness(
          detail.round_freshness ??
            detail.freshness ??
            detail.f ??
            row?.round_freshness ??
            row?.freshness,
        ),
      };
    });
  }

  return [
    {
      country: row?.admin_0_name || row?.country || row?.iso3 || "",
      operation: row?.operation || "",
      round: row?.round || row?.round_number || "",
      date: row?.reporting_date || "",
      freshness: getDataFreshness(
        row?.round_freshness ?? row?.freshness,
      ),
    },
  ];
};

const buildDataCoverageUrl = (row, detail) => {
  const country =
    row?.iso3 ||
    row?.admin_0_pcode ||
    getIso3ByCountryName(detail.country) ||
    detail.country ||
    "";
  const operation = detail.operation || row?.operation || "";
  const year = row?.year || "";
  const round = detail.round || row?.round || row?.round_number || "";

  if (!country) return "#";

  const params = new URLSearchParams({
    entitycountry: country,
    entityoperation: operation,
    entityyear: String(year),
    entityround: String(round),
    searchindicator: "idp",
    searchcountry: country,
  });

  return `http://localhost:3001/?${params.toString()}`;
};

export const normalizeSupportOperationRounds = (value) => {
  const parsedValue = parseJsonValue(value);

  if (Array.isArray(parsedValue)) return parsedValue;
  if (!parsedValue) return [];
  if (Array.isArray(parsedValue.rows)) return parsedValue.rows;
  if (Array.isArray(parsedValue.rounds)) return parsedValue.rounds;
  if (Array.isArray(parsedValue.data)) return parsedValue.data;

  return [parsedValue];
};

const getSupportRoundDate = (round) => {
  if (round?.reporting_date_range) return round.reporting_date_range;
  if (Array.isArray(round?.reporting_dates)) {
    return round.reporting_dates.filter(Boolean).join(", ");
  }
  return round?.reporting_dates || round?.reporting_date || round?.date || "";
};

const dedupeSourcesByOrder = (sourceList) => {
  const seenOrders = new Set();

  return sourceList.filter((source) => {
    const order = Number(source?.order || 0);
    if (seenOrders.has(order)) return false;
    seenOrders.add(order);
    return true;
  });
};

// The Data Sources panel entries for summary_support citations must be
// unique PROVENANCE sources (country + operation + round + reporting date),
// not one card per support row/figure. A row backed by several rounds
// contributes several entries; several rows backed by the same round
// contribute exactly one. The annotator (AIResponse.js) already resolved
// each row's unique rounds to their (shared, first-occurrence) datasource
// numbers on `source.provenanceRounds` — this just flattens and dedupes
// across all rows by that number. Calculated/derived rows (e.g. an average)
// carry no provenanceRounds, so they contribute no panel entry.
export const getSummarySupportSources = (sources = []) => {
  const seenOrders = new Set();
  const entries = [];

  sources
    .filter((source) => source?.type === "summary_support" && source?.row)
    .forEach((source) => {
      (source.provenanceRounds || []).forEach(({ order, round }) => {
        if (seenOrders.has(order)) return;

        seenOrders.add(order);
        entries.push({ order, rowId: source.rowId, round });
      });
    });

  return entries.sort((left, right) => Number(left.order) - Number(right.order));
};

export const getSummarySupportSourceRounds = (source) =>
  normalizeSupportOperationRounds(source?.row?.source_operation_rounds).filter(Boolean);

export const formatSupportSourceRoundLabel = (round) => {
  const country = round?.country || "Unknown country";
  const operation = round?.operation || "Unknown operation";
  const roundNumber = round?.round_number ?? round?.round ?? "unknown";

  return `${country} \u2192 ${operation} Round ${roundNumber}`;
};

export const formatSupportRoundDate = (round) => {
  const value = getSupportRoundDate(round);
  if (!value) return "";
  if (String(value).includes(",") || String(value).includes(" to ")) return String(value);
  return formatSourceDate(value);
};

const getSupportRoundFreshness = (round) => {
  const label =
    round?.round_freshness_label ||
    round?.freshness_label ||
    round?.freshnessLabel;
  if (label) {
    return {
      label,
      color: String(round?.round_freshness_color || round?.freshness_color || "gray"),
      description: label,
    };
  }

  const rawFreshness =
    round?.round_freshness ??
    round?.freshness ??
    round?.fr ??
    round?.f;
  if (rawFreshness === null || rawFreshness === undefined || rawFreshness === "") {
    return null;
  }

  return getDataFreshness(rawFreshness) || {
    label: String(rawFreshness),
    color: "gray",
    description: String(rawFreshness),
  };
};

const SummarySupportSourceList = ({
  activeSourceOrder,
  onSourceClick,
  onSourceHover,
  onSourceLeave,
  sources,
}) => {
  if (!sources.length) return null;

  return (
    <ol className={classes.support_source_list}>
      {sources.map((source) => {
        const isActive = String(activeSourceOrder || "")
          .split(",")
          .includes(String(source.order));
        const date = formatSupportRoundDate(source.round);
        const freshness = getSupportRoundFreshness(source.round);

        return (
          <li
            key={source.order}
            value={source.order}
            className={isActive ? classes.support_source_active : undefined}
          >
            <button
              type="button"
              className={`${classes.support_source_item}${
                isActive ? ` ${classes.support_source_item_active}` : ""
              }`}
              onClick={() => onSourceClick?.(source.rowId, source.round)}
              onMouseEnter={() => onSourceHover?.(source)}
              onMouseLeave={() => onSourceLeave?.()}
            >
              <span className={classes.support_source_label}>
                {formatSupportSourceRoundLabel(source.round)}
              </span>
              {(date || freshness) && (
                <span className={classes.support_source_round_secondary}>
                  {date && <span>{date}</span>}
                  {freshness && (
                    <span
                      className={`${classes.freshness} ${
                        classes[`freshness_${freshness.color}`] || ""
                      }`}
                      title={freshness.description || freshness.label}
                    >
                      {freshness.label}
                    </span>
                  )}
                </span>
              )}
            </button>
          </li>
        );
      })}
    </ol>
  );
};
function extractOperationMeta(row) {
  const first = getOperationItems(row?.operation_data)[0];
  const operation = parseJsonValue(first) || {};

  return {
    round: operation.r ?? operation.round ?? operation.round_number ?? null,
    iso3: operation.iso3 ?? operation.admin_0_pcode ?? null,
    operation: operation.o ?? operation.operation ?? null,
  };
}

// The single, proven-working "source record -> load on map" behavior.
// Every entry point (Data Sources, ranking "Visualize data", summary
// support round rows) must funnel through this instead of keeping its
// own copy of the selection dispatches.
export const applySourceSelectionToMap = ({
  dispatch,
  currentSelection,
  targetCountry,
  targetPcode,
  targetOperation,
  targetRound,
  rowYear,
  rowCause,
  populationType,
}) => {
  // The map only reloads (and clears its loader) when one of these
  // map-driving selections actually changes. If nothing changes, don't
  // turn the loader on — it would never get switched off.
  const mapWillReload =
    currentSelection.country !== targetCountry ||
    currentSelection.countryPcode !== targetPcode ||
    currentSelection.admin1 !== "" ||
    currentSelection.admin2 !== "" ||
    (Number.isFinite(rowYear) && Number(currentSelection.year) !== rowYear) ||
    currentSelection.cause !== rowCause ||
    currentSelection.operation !== targetOperation;

  if (mapWillReload) {
    dispatch(selectionsSliceActions.setMapLoading(true));
  }
  dispatch(selectionsSliceActions.setCountry(targetCountry));
  dispatch(selectionsSliceActions.setCountryPcode(targetPcode));
  dispatch(selectionsSliceActions.setAdmin1(""));
  dispatch(selectionsSliceActions.setAdmin2(""));
  // Align the dashboard filters with the cited data so the map and
  // figures reflect the same year/cause the answer is about.
  if (Number.isFinite(rowYear)) {
    dispatch(selectionsSliceActions.setYear(rowYear));
  }
  dispatch(selectionsSliceActions.setCause(rowCause));
  dispatch(selectionsSliceActions.setOperation(targetOperation));
  dispatch(selectionsSliceActions.setRound(targetRound));
  // Isolate the figures to the specific round the user clicked, instead
  // of collapsing all rounds of this operation together.
  dispatch(selectionsSliceActions.setRoundFilter(targetRound));
  // Match the map's population toggle to the question (male/female/total).
  dispatch(selectionsSliceActions.setPopulationType(populationType));
};

const AIResponseSource = ({
  activeSourceOrder = null,
  children,
  collapseSignal,
  onSourceHover,
  onSourceLeave,
  populationType = "total",
  promptId = null,
  sources = [],
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [openMenuId, setOpenMenuId] = useState("");
  const [menuPlacement, setMenuPlacement] = useState("down");
  const [apiConfigUrl, setApiConfigUrl] = useState("");
  const sourceRef = useRef(null);
  const dispatch = useDispatch();
  const selectedCountry = useSelector((state) => state.selection.country);
  const selectedCountryPcode = useSelector(
    (state) => state.selection.countrypcode,
  );
  const selectedAdmin1 = useSelector((state) => state.selection.admin1);
  const selectedAdmin2 = useSelector((state) => state.selection.admin2);
  const selectedYear = useSelector((state) => state.selection.year);
  const selectedCause = useSelector((state) => state.selection.cause);
  const selectedOperation = useSelector((state) => state.selection.operation);
  const querySources = sources.filter((source) => source?.type !== "summary_support");
  // Display list only: rows sharing a datasource (same .order) collapse to one card.
  // querySources itself stays undeduped so per-rowId lookups below keep resolving
  // every individual citation to its own row.
  const dedupedQuerySources = dedupeSourcesByOrder(querySources);
  const summarySupportSources = getSummarySupportSources(sources);
  const hasSummarySupportSources = summarySupportSources.length > 0;

  useEffect(() => {
    const closeOnOutsideClick = (event) => {
      if (!sourceRef.current || sourceRef.current.contains(event.target)) {
        return;
      }

      setOpenMenuId("");
      onSourceLeave?.();
    };

    document.addEventListener("mousedown", closeOnOutsideClick);
    return () => document.removeEventListener("mousedown", closeOnOutsideClick);
  }, [onSourceLeave]);

  useEffect(() => {
    if (!collapseSignal) return;

    setIsOpen(false);
    setOpenMenuId("");
    onSourceLeave?.();
  }, [collapseSignal, onSourceLeave]);

  if (!querySources.length && !hasSummarySupportSources) {
    return (
      <section className={classes.sources} aria-label="AI response tools">
        <div className={classes.toolbar}>{children}</div>
      </section>
    );
  }

  const handleSupportRoundClick = (summaryRowId, sourceOperationRound) => {
    if (!summaryRowId || !sourceOperationRound) return;

    const targetCountry = sourceOperationRound?.country || "";
    const targetPcode = getIso3ByCountryName(targetCountry) || "";
    const targetOperation = sourceOperationRound?.operation || "All";
    const targetRound =
      sourceOperationRound?.round_number ?? sourceOperationRound?.round ?? null;
    const rowYear = Number(sourceOperationRound?.year);
    const rowCause = sourceOperationRound?.reason_for_displacement || "All";

    applySourceSelectionToMap({
      dispatch,
      currentSelection: {
        country: selectedCountry,
        countryPcode: selectedCountryPcode,
        admin1: selectedAdmin1,
        admin2: selectedAdmin2,
        year: selectedYear,
        cause: selectedCause,
        operation: selectedOperation,
      },
      targetCountry,
      targetPcode,
      targetOperation,
      targetRound,
      rowYear,
      rowCause,
      populationType,
    });
  };
  const handleVisualizeDataClick = (rowId, detail) => {
          let sourceRow = querySources.filter((r) => String(r.rowId) === String(rowId));
          if (sourceRow.length === 0)
              return;
          sourceRow = sourceRow[0];

          const operation_data = extractOperationMeta(sourceRow.row);
          const countryIso3 =
            operation_data.iso3 ||
            sourceRow.row.iso3 ||
            sourceRow.row.admin_0_pcode ||
            getIso3ByCountryName(sourceRow.row.admin_0_name);

          const rowYear = Number(
            sourceRow.row.year ?? sourceRow.row.Year ?? sourceRow.row.YEAR,
          );
          const rowCause =
            sourceRow.row.reason_for_displacement ||
            sourceRow.row.reason ||
            sourceRow.row.cause ||
            "All";
          const targetCountry = sourceRow.row.admin_0_name;
          const targetPcode = countryIso3 || "";
          // Use the specific operation/round the user clicked, so the map and
          // figures reflect that round — not just the row's first operation.
          const targetOperation =
            detail?.operation ||
            operation_data.operation ||
            sourceRow.row.operation ||
            "All";
          const targetRound =
            detail?.round ?? operation_data.round ?? null;

          applySourceSelectionToMap({
            dispatch,
            currentSelection: {
              country: selectedCountry,
              countryPcode: selectedCountryPcode,
              admin1: selectedAdmin1,
              admin2: selectedAdmin2,
              year: selectedYear,
              cause: selectedCause,
              operation: selectedOperation,
            },
            targetCountry,
            targetPcode,
            targetOperation,
            targetRound,
            rowYear,
            rowCause,
            populationType,
          });
          setOpenMenuId("");
        };

  const handleApiConfigClick = (rowId) => {
    let sourceRow = querySources.filter((r) => String(r.rowId) === String(rowId));
    if (sourceRow.length === 0) return;
    sourceRow = sourceRow[0];

    const operation_data = extractOperationMeta(sourceRow.row);
    const countryIso3 =
      operation_data.iso3 ||
      sourceRow.row.iso3 ||
      sourceRow.row.admin_0_pcode ||
      getIso3ByCountryName(sourceRow.row.admin_0_name);
    const rowYear =
      sourceRow.row.year ?? sourceRow.row.Year ?? sourceRow.row.YEAR;
    const rowCause =
      sourceRow.row.reason_for_displacement ||
      sourceRow.row.reason ||
      sourceRow.row.cause ||
      "All";

    // A cited source is country/operation/round level, so this maps to the
    // admin1 endpoint (Admin0Pcode), matching the map's "Get data" at Admin 1.
    const url = buildDtmApiUrl({
      countryPcode: countryIso3 || "",
      year: rowYear,
      operation: operation_data.operation || sourceRow.row.operation || "All",
      cause: rowCause,
    });

    setApiConfigUrl(url);
    setOpenMenuId("");
  };

  const toggleMenu = (menuId, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const estimatedMenuHeight = 132;
    const availableBelow = window.innerHeight - rect.bottom;

    setMenuPlacement(availableBelow < estimatedMenuHeight ? "up" : "down");
    setOpenMenuId((current) => (current === menuId ? "" : menuId));
  };

  return (
    <section
      ref={sourceRef}
      className={classes.sources}
      data-prompt-id={promptId || undefined}
      aria-label="Data sources"
    >
      <div className={classes.toolbar}>
        <button
          type="button"
          className={classes.title_button}
          aria-expanded={isOpen}
          aria-label="Data sources"
          title="Data sources"
          onClick={() => {
            setIsOpen((current) => !current);
            setOpenMenuId("");
            onSourceLeave?.();
          }}
        >
          <FileIcon />
          <span>Data sources</span>
          <span className={`${classes.chevron}${isOpen ? ` ${classes.chevron_open}` : ""}`} />
        </button>
        {children}
      </div>

      {isOpen && (
        <div className={classes.panel_content}>
          {hasSummarySupportSources && (
            <SummarySupportSourceList
              activeSourceOrder={activeSourceOrder}
              sources={summarySupportSources}
              onSourceClick={handleSupportRoundClick}
              onSourceHover={(source) => {
                onSourceHover?.(source.order, source.rowId, "blue");
              }}
              onSourceLeave={onSourceLeave}
            />
          )}
          {dedupedQuerySources.length > 0 && (
            <ol className={classes.list}>
              {dedupedQuerySources.map((source) => (
                <li key={`${source.order}-${source.rowId}`} className={classes.item}>
                  <div className={classes.detail_list}>
                    {getDetailItems(source.row).map((detail, index) => {
                      const menuId = `${source.rowId}-${index}`;
                      const isMenuOpen = openMenuId === menuId;
                      const dataCoverageUrl = buildDataCoverageUrl(source.row, detail);

                      return (
                        <div
                          key={menuId}
                          className={classes.source_wrap}
                        >
                          <button
                            type="button"
                            className={classes.source_card}
                            aria-expanded={isMenuOpen}
                            onMouseEnter={() =>
                              onSourceHover?.(
                                source.order,
                                source.rowId,
                                detail.freshness?.color || "gray",
                              )
                            }
                            onMouseLeave={() => onSourceLeave?.()}
                            onClick={(event) => toggleMenu(menuId, event)}
                          >
                            <span>{detail.country || "Unknown country"}</span>
                            <span aria-hidden="true">{" -> "}</span>
                            <span>{detail.operation || "Unknown operation"}</span>
                            <span> Round {detail.round || "unknown"}</span>
                            {detail.date && (
                              <span>{formatSourceDate(detail.date)}</span>
                            )}
                            {detail.freshness && (
                              <span
                                className={`${classes.freshness} ${
                                  classes[`freshness_${detail.freshness.color}`]
                                }`}
                                title={detail.freshness.description}
                              >
                                {detail.freshness.label}
                              </span>
                            )}

                          </button>

                          {isMenuOpen && (
                            <div
                              className={`${classes.menu}${
                                menuPlacement === "up" ? ` ${classes.menu_up}` : ""
                              }`}
                            >
                              <button type="button" className={classes.menu_item} onClick={() => handleVisualizeDataClick(source.rowId, detail)}>
                                Visualize this data
                              </button>
                              {/*<a
                                className={classes.menu_item}
                                href={dataCoverageUrl}
                                target="_blank"
                                rel="noreferrer"
                                onClick={() => {
                                  setOpenMenuId("");
                                }}
                              >
                                Explore with API data coverage
                              </a>*/}
                              <button
                                type="button"
                                className={classes.menu_item}
                                onClick={() => handleApiConfigClick(source.rowId)}
                              >
                                API connection config
                              </button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}
      <ApiConfigModal
        open={Boolean(apiConfigUrl)}
        url={apiConfigUrl}
        onClose={() => setApiConfigUrl("")}
      />
    </section>
  );
};

export default AIResponseSource;
